import React from 'react';
import { useTranslation } from 'react-i18next';

interface DocumentsSectionProps {
  uploadedFiles: string[];
  isUploading: boolean;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeleteFile: (fileUrl: string, index: number) => void;
  canManage?: boolean;
}

export function DocumentsSection({ 
  uploadedFiles, 
  isUploading, 
  handleFileUpload, 
  handleDeleteFile,
  canManage = true
}: DocumentsSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="tab-panel">
      <h3 className="form-block-title" style={{ color: '#000000' }}>
        📄 {t('patientDetail.documentUpload')}
      </h3>
      <div className="form-fields-vertical">
        <div className="form-group">
          {canManage && (
            <>
          <label htmlFor="fileUpload" style={{ color: '#000000' }}>
            {t('patientDetail.uploadDocuments')}
          </label>
          <div className="file-upload-container">
            <input
              type="file"
              id="fileUpload"
              multiple
              onChange={handleFileUpload}
              disabled={isUploading}
              style={{ display: 'none' }}
            />
            <label
              htmlFor="fileUpload"
              className={`file-upload-label ${isUploading ? 'uploading' : ''}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px 20px',
                border: '2px dashed #4a4a4a',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                color: '#333333',
                cursor: isUploading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: isUploading ? 0.6 : 1
              }}
            >
              {isUploading ? (
                <>
                  <div className="loading-spinner" style={{ width: '20px', height: '20px' }}></div>
                  <span>{t('patientDetail.uploading')}</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '20px' }}>📁</span>
                  <span>{t('patientDetail.clickToUpload')}</span>
                </>
              )}
            </label>
          </div>
            </>
          )}
          
          {uploadedFiles.length > 0 && (
            <div style={{ 
              marginTop: '16px',
              maxHeight: '320px',
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              backgroundColor: '#fafafa'
            }}>
              {uploadedFiles.map((fileUrl, index) => {
                const fileName = fileUrl.split('/').pop() || '';
                const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(fileName);
                const fileExtension = fileName.split('.').pop()?.toUpperCase() || 'FILE';
                
                return (
                  <div 
                    key={index} 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      borderBottom: index < uploadedFiles.length - 1 ? '1px solid #e0e0e0' : 'none',
                      backgroundColor: 'white',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    {/* File icon/preview */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '6px',
                      backgroundColor: isImage ? '#e8f4fd' : '#f0f0f0',
                      overflow: 'hidden'
                    }}>
                      {isImage ? (
                        <img 
                          src={fileUrl} 
                          alt={fileName}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            // If image fails to load (CORS or 404), show fallback icon
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent && !parent.querySelector('.fallback-icon')) {
                              const fallback = document.createElement('span');
                              fallback.className = 'fallback-icon';
                              fallback.textContent = '🖼️';
                              fallback.style.fontSize = '24px';
                              fallback.title = 'Image failed to load (CORS or network error)';
                              parent.appendChild(fallback);
                            }
                          }}
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <span style={{ fontSize: '24px' }}>📄</span>
                      )}
                    </div>

                    {/* File info */}
                    <div style={{ 
                      flex: 1, 
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {fileName}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        textTransform: 'uppercase'
                      }}>
                        {fileExtension}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px',
                      flexShrink: 0
                    }}>
                      {/* Download button */}
                      <button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = fileUrl;
                          link.download = fileName;
                          link.click();
                        }}
                        style={{
                          background: 'transparent',
                          color: '#666',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          width: '36px',
                          height: '36px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f5f5f5';
                          e.currentTarget.style.borderColor = '#999';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.borderColor = '#ddd';
                        }}
                        title="Download file"
                      >
                        ⬇️
                      </button>

                      {/* Delete button - only show if user can manage documents */}
                      {canManage && (
                      <button
                        onClick={() => handleDeleteFile(fileUrl, index)}
                        style={{
                          background: 'transparent',
                          color: '#666',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          width: '36px',
                          height: '36px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f5f5f5';
                          e.currentTarget.style.borderColor = '#999';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.borderColor = '#ddd';
                        }}
                        title="Delete file"
                      >
                        🗑️
                      </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentsSection;

